import {
  DeleteOutlined,
  DownOutlined,
  EyeOutlined,
  ReloadOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Input,
  MenuProps,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';

import {
  cleanupApiLogsAsAdmin,
  deleteApiLogAsAdmin,
  fetchApiLogAsAdmin,
  fetchApiLogsAsAdmin,
} from '@/apis';
import { ApiLogDetailModal } from '@/components/Admin/Modal';
import PageHeader from '@/components/Admin/PageHeader';
import StateRenderer from '@/components/Admin/StateRenderer';
import TableContainer from '@/components/Admin/TableContainer';
import { MessageType } from '@/enums';
import { useAppFeedback } from '@/hooks';
import {
  ApiLog,
  ApiLogFilters,
  ApiLogListItem,
  LogType,
} from '@/types/apis/admin';
import { formatDateTime } from '@/utils/dateFormatter';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface FilterState extends ApiLogFilters {
  page: number;
  limit: number;
}

const pageSize = 10;

const AdminApiLogsPage = () => {
  const [apiLogs, setApiLogs] = useState<ApiLogListItem[]>([]);
  const [filters, setFilters] = useState<Omit<FilterState, 'page' | 'limit'>>(
    {},
  );
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedApiLog, setSelectedApiLog] = useState<ApiLog | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCleanupMenu, setShowCleanupMenu] = useState(false);

  const { showMessage, showConfirm } = useAppFeedback();

  const loadApiLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchApiLogsAsAdmin({
        page,
        limit: pageSize,
        ...filters,
      });
      setApiLogs(response.data.items);
      setTotal(response.data.total);
    } catch (err) {
      const errorMessage = '載入 API 日誌失敗，請稍後再試';
      setError(errorMessage);
      showMessage(errorMessage, MessageType.Error);
      console.error('Error loading API logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, showMessage]);

  useEffect(() => {
    loadApiLogs();
  }, [loadApiLogs]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showCleanupMenu) {
        const target = event.target as Element;
        if (!target.closest('.cleanup-menu-container')) {
          setShowCleanupMenu(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showCleanupMenu]);

  const handleFilterChange = (
    key: keyof Omit<FilterState, 'page' | 'limit'>,
    value: unknown,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPage(1); // Reset to first page when filters change
  };

  const cleanupMenuItems: MenuProps['items'] = [
    {
      key: '1',
      label: '清理 1 天前',
      onClick: () => handleCleanupOldLogs(1),
    },
    {
      key: '7',
      label: '清理 7 天前',
      onClick: () => handleCleanupOldLogs(7),
    },
    {
      key: '30',
      label: '清理 30 天前',
      onClick: () => handleCleanupOldLogs(30),
    },
    {
      type: 'divider',
    },
    {
      key: 'all',
      label: '清理全部',
      danger: true,
      onClick: () => handleCleanupOldLogs(undefined, true),
    },
  ];

  const handleDeleteApiLog = (apiLogId: string) => {
    showConfirm(
      '確認刪除',
      '確定要刪除此 API 日誌嗎？此操作無法復原。',
      async () => {
        try {
          await deleteApiLogAsAdmin(apiLogId);
          showMessage('API 日誌刪除成功', MessageType.Success);
          await loadApiLogs();
        } catch (err) {
          showMessage('刪除 API 日誌失敗，請稍後再試', MessageType.Error);
          console.error('Error deleting API log:', err);
        }
      },
      undefined,
      '確定刪除',
      '取消',
      'danger',
    );
  };

  const handleCleanupOldLogs = (days?: number, isAll?: boolean) => {
    let confirmMessage = '';
    let daysToCleanup = days;

    if (isAll) {
      confirmMessage = '確定要清理所有 API 日誌嗎？此操作無法恢復！';
      daysToCleanup = 0;
    } else {
      const dayText = days === 1 ? '1 天' : `${days} 天`;
      confirmMessage = `確定要清理 ${dayText}前的舊 API 日誌嗎？`;
    }

    showConfirm(
      '確認清理',
      confirmMessage,
      async () => {
        try {
          const response = await cleanupApiLogsAsAdmin(daysToCleanup);
          showMessage(
            `成功清理 ${response.data.deletedCount} 條舊 API 日誌`,
            MessageType.Success,
          );
          await loadApiLogs();
        } catch (err) {
          showMessage('清理舊 API 日誌失敗，請稍後再試', MessageType.Error);
          console.error('Error cleaning up old logs:', err);
        }
      },
      undefined,
      '確定清理',
      '取消',
      'danger',
    );
  };

  const handleViewApiLogDetail = async (apiLogListItem: ApiLogListItem) => {
    try {
      setLoading(true);
      const response = await fetchApiLogAsAdmin(apiLogListItem.id);
      setSelectedApiLog(response.data);
      setIsModalOpen(true);
    } catch (err) {
      showMessage('載入 API 日誌詳情失敗', MessageType.Error);
      console.error('Error loading API log detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedApiLog(null);
  };

  const formatTimestamp = (timestamp: number) => {
    return formatDateTime(new Date(timestamp)).replace(/\//g, '-');
  };

  const getTypeColor = (type: LogType) => {
    // ERROR level types
    if (
      [
        LogType.API_ERROR,
        LogType.DATABASE,
        LogType.VALIDATION,
        LogType.AUTHENTICATION,
        LogType.AUTHORIZATION,
        LogType.NETWORK,
        LogType.SYSTEM,
        LogType.UNKNOWN,
      ].includes(type)
    ) {
      return 'red';
    }

    // WARN level types
    if ([LogType.API_WARN, LogType.BUSINESS].includes(type)) {
      return 'orange';
    }

    // DEBUG level types
    if (type === LogType.DEBUG) {
      return 'default';
    }

    // INFO level types (default)
    return 'blue';
  };

  const apiLogColumns: ColumnsType<ApiLogListItem> = [
    {
      title: '類型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: LogType) => (
        <Tag color={getTypeColor(type)}>
          {type.replace(/_/g, ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: '訊息',
      key: 'message',
      ellipsis: {
        showTitle: true,
      },
      render: (_, apiLog) => (
        <div>
          {apiLog.errorName && <Text strong>[{apiLog.errorName}] </Text>}
          <Text>{apiLog.message}</Text>
        </div>
      ),
    },
    {
      title: '用戶',
      dataIndex: 'userId',
      key: 'user',
      width: 200,
      render: (userId: string) => <Text type="secondary">{userId || '-'}</Text>,
    },
    {
      title: '時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (createdAt: number) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatTimestamp(createdAt)}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, apiLog) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewApiLogDetail(apiLog)}
            title="查看詳細資訊"
            size="small"
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteApiLog(apiLog.id)}
            title="刪除 API 日誌"
            danger
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="API 日誌管理"
        description="監控和管理 API 請求，追蹤系統使用狀態。"
      />

      <TableContainer
        title="API 日誌列表"
        actions={
          <Space>
            <Button
              icon={showFilters ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setShowFilters(!showFilters)}
            >
              篩選器
            </Button>
            <Button icon={<ReloadOutlined />} onClick={loadApiLogs}>
              重新載入
            </Button>
            <Dropdown
              menu={{ items: cleanupMenuItems }}
              placement="bottomRight"
            >
              <Button>
                清理舊日誌
                <DownOutlined />
              </Button>
            </Dropdown>
          </Space>
        }
      >
        <div>
          {showFilters && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8} lg={6}>
                  <div>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>
                      類型
                    </Text>
                    <Select
                      value={filters.type || undefined}
                      onChange={(value) => handleFilterChange('type', value)}
                      placeholder="全部類型"
                      style={{ width: '100%' }}
                      allowClear
                    >
                      {Object.values(LogType).map((type) => (
                        <Option key={type} value={type}>
                          {type.toUpperCase()}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
                <Col xs={24} md={8} lg={6}>
                  <div>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>
                      日期範圍
                    </Text>
                    <RangePicker
                      value={
                        filters.startDate && filters.endDate
                          ? [dayjs(filters.startDate), dayjs(filters.endDate)]
                          : null
                      }
                      onChange={(dates) => {
                        if (dates) {
                          handleFilterChange(
                            'startDate',
                            dates[0]?.format('YYYY-MM-DD'),
                          );
                          handleFilterChange(
                            'endDate',
                            dates[1]?.format('YYYY-MM-DD'),
                          );
                        } else {
                          handleFilterChange('startDate', undefined);
                          handleFilterChange('endDate', undefined);
                        }
                      }}
                      style={{ width: '100%' }}
                    />
                  </div>
                </Col>
                <Col xs={24} md={8} lg={6}>
                  <div>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>
                      用戶 ID
                    </Text>
                    <Input
                      value={filters.userId || ''}
                      onChange={(e) =>
                        handleFilterChange(
                          'userId',
                          e.target.value || undefined,
                        )
                      }
                      placeholder="輸入用戶 ID"
                    />
                  </div>
                </Col>
                <Col xs={24} md={12} lg={6}>
                  <div>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>
                      搜尋關鍵字
                    </Text>
                    <Input
                      value={filters.search || ''}
                      onChange={(e) =>
                        handleFilterChange(
                          'search',
                          e.target.value || undefined,
                        )
                      }
                      placeholder="搜尋訊息、用戶 ID、請求 URL 等"
                    />
                  </div>
                </Col>
              </Row>
            </Card>
          )}

          <StateRenderer
            loading={loading}
            error={error}
            empty={!apiLogs || apiLogs.length === 0}
            emptyMessage="沒有找到 API 日誌"
          >
            <Table
              scroll={{ x: 'max-content' }}
              columns={apiLogColumns}
              dataSource={apiLogs || []}
              rowKey="id"
              pagination={{
                current: page,
                pageSize: pageSize,
                total: total,
                onChange: setPage,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `第 ${range[0]}-${range[1]} 筆，共 ${total} 筆`,
              }}
              size="small"
              loading={loading}
            />
          </StateRenderer>
        </div>
      </TableContainer>

      <ApiLogDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        apiLog={selectedApiLog}
      />
    </>
  );
};

export default AdminApiLogsPage;
